/** The "big switches": pause all withdrawals, pause swaps (D55), and the signer lock (D50). */
import { Badge, Button, Card, CardTitle, Field, Input, Notice } from "@custos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Lock, OctagonPause, Play } from "lucide-react";
import { useState } from "react";
import { api } from "../lib/api";
import { errorMessage } from "../lib/errors";
import { OWNER_ONLY_NOTE, useCan } from "../lib/permissions";
import { qk, useOverview, useSigner } from "../lib/queries";
import { StepUpModal } from "./StepUpModal";

export function PauseControl() {
  const overview = useOverview();
  const queryClient = useQueryClient();
  const [resumeOpen, setResumeOpen] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: qk.overview });
  // EMERGENCY: one click, no 2FA (pausing is always safe).
  const pause = useMutation({ mutationFn: () => api.pauseWithdrawals(), onSuccess: refresh });
  const paused = overview.data?.withdrawalsPaused ?? false;
  const can = useCan();

  return (
    <div className="flex flex-col gap-3">
      {paused ? (
        <>
          <Badge tone="danger" className="self-start text-sm">
            Withdrawals PAUSED
          </Badge>
          {can("pause.resume") ? (
            <Button variant="primary" onClick={() => setResumeOpen(true)}>
              <Play className="size-4" aria-hidden /> Resume withdrawals
            </Button>
          ) : (
            <p className="text-sm opacity-80">{OWNER_ONLY_NOTE} (resume)</p>
          )}
        </>
      ) : !can("emergency.pause") ? null : (
        <Button
          variant="danger"
          className="min-h-14 text-base font-semibold"
          loading={pause.isPending}
          onClick={() => pause.mutate()}
        >
          <OctagonPause className="size-5" aria-hidden /> PAUSE ALL WITHDRAWALS
        </Button>
      )}
      {pause.error && <Notice tone="danger">{errorMessage(pause.error)}</Notice>}
      <StepUpModal
        open={resumeOpen}
        onOpenChange={setResumeOpen}
        title="Resume withdrawals?"
        description="Queued withdrawals will be signed and sent again."
        confirmLabel="Resume withdrawals"
        onConfirm={(code) => api.resumeWithdrawals(code)}
        onDone={refresh}
      />
    </div>
  );
}

/** Separate from the withdrawal pause (owner's choice, D55). Pausing: one click; resuming: 2FA. */
export function SwapPauseControl() {
  const overview = useOverview();
  const queryClient = useQueryClient();
  const [resumeOpen, setResumeOpen] = useState(false);
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.overview }),
      queryClient.invalidateQueries({ queryKey: qk.limits }),
    ]);
  const pause = useMutation({ mutationFn: () => api.pauseSwaps(), onSuccess: refresh });
  const paused = overview.data?.swapsPaused ?? false;
  const can = useCan();

  return (
    <div className="flex flex-col gap-3">
      {paused ? (
        <>
          <Badge tone="danger" className="self-start text-sm">
            Swaps PAUSED
          </Badge>
          {can("pause.resume") ? (
            <Button variant="primary" onClick={() => setResumeOpen(true)}>
              <Play className="size-4" aria-hidden /> Resume swaps
            </Button>
          ) : (
            <p className="text-sm text-muted">{OWNER_ONLY_NOTE} (resume)</p>
          )}
        </>
      ) : !can("emergency.pause") ? null : (
        <Button variant="danger" loading={pause.isPending} onClick={() => pause.mutate()}>
          <OctagonPause className="size-5" aria-hidden /> Pause swaps
        </Button>
      )}
      {pause.error && <Notice tone="danger">{errorMessage(pause.error)}</Notice>}
      <StepUpModal
        open={resumeOpen}
        onOpenChange={setResumeOpen}
        title="Resume swaps?"
        description="Users can get quotes and swap again straight away."
        confirmLabel="Resume swaps"
        onConfirm={(code) => api.resumeSwaps(code)}
        onDone={refresh}
      />
    </div>
  );
}

export function SignerCard() {
  const signer = useSigner();
  const queryClient = useQueryClient();
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [passphrase, setPassphrase] = useState("");
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.signer }),
      queryClient.invalidateQueries({ queryKey: qk.overview }),
    ]);
  // Locking is always safe → one click, no 2FA.
  const lock = useMutation({ mutationFn: () => api.lockSigner(), onSuccess: refresh });
  const s = signer.data;
  const can = useCan();

  return (
    <Card className="flex flex-col gap-3" data-testid="signer-card">
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="size-5" aria-hidden /> Signer
        </CardTitle>
        {s && (
          <Badge tone={s.status === "unlocked" ? "success" : s.status === "locked" ? "warning" : "danger"}>
            {s.status === "unknown" ? "not connected" : s.status}
          </Badge>
        )}
      </div>
      {s && !s.available ? (
        <p className="text-sm text-muted">
          Panel controls are off (no <code className="font-mono">SIGNER_ADMIN_SECRET</code> for the API). Use{" "}
          <code className="font-mono">pnpm signer:unlock</code> on the server.
        </p>
      ) : s?.status === "unlocked" ? (
        <>
          <p className="text-sm text-muted">
            Withdrawals and sweeps are being signed.
            {s.unlockedAt && ` Unlocked ${new Date(s.unlockedAt).toLocaleString()}.`}
          </p>
          {can("emergency.pause") && (
            <Button variant="secondary" loading={lock.isPending} onClick={() => lock.mutate()}>
              <Lock className="size-4" aria-hidden /> Lock signer now
            </Button>
          )}
        </>
      ) : s?.status === "locked" ? (
        <>
          <p className="text-sm text-muted">Withdrawals and sweeps wait until the signer is unlocked.</p>
          {can("signer.unlock") ? (
            <Button onClick={() => setUnlockOpen(true)}>
              <KeyRound className="size-4" aria-hidden /> Unlock signer
            </Button>
          ) : (
            <p className="text-sm text-muted">{OWNER_ONLY_NOTE} (unlock)</p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted">The signer isn't reachable. Is it running?</p>
      )}
      {lock.error && <Notice tone="danger">{errorMessage(lock.error)}</Notice>}
      <StepUpModal
        open={unlockOpen}
        onOpenChange={(open) => {
          setUnlockOpen(open);
          if (!open) setPassphrase("");
        }}
        title="Unlock the signer"
        description="Your passphrase goes to the signer once and is never stored or logged."
        confirmLabel="Unlock signer"
        canConfirm={passphrase.length > 0}
        onConfirm={(code) => api.unlockSigner(passphrase, code)}
        onDone={() => {
          setPassphrase("");
          void refresh();
        }}
      >
        <Notice tone="warning">
          Only unlock from a trusted computer over HTTPS. The safest way is still{" "}
          <code className="font-mono">pnpm signer:unlock</code> over SSH.
        </Notice>
        <Field label="Unlock passphrase">
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="off"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
            />
          )}
        </Field>
      </StepUpModal>
    </Card>
  );
}
